/**
 * ScopedGeoView — Country / Subdivision / City history landing pages.
 *
 * Lead Foundation Brief §2, §4: every entity has a page. Active when its
 * founding chain is published; coming-soon when not. Empty states are
 * statements of intentional sequencing, not apologies.
 */
import { Sparkles, Clock } from 'lucide-react';

export default function ScopedGeoView({ scope, kind, onChainSelect }) {
  const target =
    kind === 'country' ? scope.country :
    kind === 'subdivision' ? scope.subdivision :
    kind === 'city' ? scope.city : null;

  if (!target) {
    return (
      <div className="p-6 md:p-8 max-w-3xl">
        <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-3">
          {kind === 'country' ? 'Country History'
            : kind === 'subdivision' ? 'Subdivision History'
            : 'City History'}
        </h2>
        <p className="font-mono text-sm md:text-base text-white/80 leading-relaxed">
          {kind === 'country' && 'Pick a country from the dropdown above. Every nation on earth is listed. Active ones have walked their founding chain. The rest are coming as the spine reaches them.'}
          {kind === 'subdivision' && 'Pick a country first, then a subdivision. Country-specific terminology applies — State, Province, Prefecture, Canton, Oblast, depending on the nation.'}
          {kind === 'city' && 'Pick a country and subdivision first, then a city. Each city earns its own history when the spine walks through the migration and settlement chains that led to that ground.'}
        </p>
      </div>
    );
  }

  const isPublished = target.chain_status === 'published';
  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid={`scoped-${kind}`}>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="font-hud text-[10px] md:text-xs tracking-widest uppercase"
          style={{ color: isPublished ? '#D4A84A' : '#52525B' }}
        >
          {kind === 'subdivision' ? (target.subdivision_label || 'Region') : kind}
        </span>
      </div>
      <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-4">
        {target.name}
      </h2>

      {isPublished ? (
        <div className="space-y-4">
          <p className="font-mono text-sm md:text-base text-white/85 leading-relaxed">
            Founding chain walked. Click to begin.
          </p>
          {target.founding_chain_id && (
            <button
              data-testid={`open-founding-chain-${target.slug}`}
              onClick={() => onChainSelect({ id: target.founding_chain_id, subject: target.name })}
              className="font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 inline-flex items-center gap-2"
            >
              <Sparkles size={14} /> WALK THE FOUNDING CHAIN
            </button>
          )}
        </div>
      ) : (
        <div className="border border-[#D4A84A]/15 bg-[#D4A84A]/03 rounded p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[#D4A84A]" />
            <span className="font-hud text-[10px] md:text-xs tracking-widest text-[#D4A84A] uppercase">Coming soon</span>
          </div>
          <p className="font-mono text-sm md:text-base text-white/85 leading-relaxed">
            {kind === 'country' && (
              <>
                {target.name}'s founding chain hasn't been walked yet. The methodology starts at MIGRATION — when humans first arrived on this land — not at political formation. The chain activates when the World History spine reaches that migration era.
              </>
            )}
            {kind === 'subdivision' && (
              <>
                {target.name}'s history activates after its country's founding chain publishes. Chronological depth first, geographic depth second.
              </>
            )}
            {kind === 'city' && (
              <>
                {target.name}'s history activates after its subdivision publishes. The platform builds depth in the order history actually happened.
              </>
            )}
          </p>
          <p className="font-mono text-xs md:text-sm text-white/65 italic">
            We start where the story actually starts — humanity before its borders.
          </p>
        </div>
      )}
    </div>
  );
}
